// AdminJerarquia — CRUD jerárquico: Instituciones / Colegios / Salones.
//
// #12: "Sede" eliminada. La Franquicia es la raíz (se gestiona en su propio
// módulo). Aquí Institución/Colegio/Salón se enlazan a una Franquicia.
//
// Visible para super-admin, admin y franquiciado. El scope se aplica desde el
// backend (Super Admin: todas las franquicias; otros: sólo la suya).
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Network, Building2, School, GraduationCap, Plus, Pencil, Trash2,
  Users as UsersIcon, X, ChevronRight,
} from "lucide-react";

const TABS = [
  { id: "institutions", label: "Instituciones", icon: Building2, testid: "tab-instituciones" },
  { id: "schools",      label: "Colegios",     icon: School,    testid: "tab-colegios" },
  { id: "classrooms",   label: "Salones",      icon: GraduationCap, testid: "tab-salones" },
];

// Nombre válido: al menos 1 letra + al menos 1 número, sólo alfanumérico + espacios + . _ -
const NAME_RE = /^(?=.*[A-Za-zÁÉÍÓÚÜÑáéíóúüñ])[A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9 ._-]+$/;
function validateName(name) {
  const v = (name || "").trim();
  if (!v) return "El nombre es obligatorio.";
  if (!NAME_RE.test(v))
    return "Debe incluir al menos 1 letra (no puede ser solo números), sin símbolos especiales (solo letras, números, espacios y . _ -).";
  return null;
}

// Concordancia gramatical de género por entidad (#5).
// Institución = femenino · Colegio / Salón = masculino.
const GENDER = {
  institutions: "f",
  schools: "m",
  classrooms: "m",
};
const g = (entity) => (GENDER[entity] === "f"
  ? { nuevo: "Nueva", creado: "creada", actualizado: "actualizada", eliminado: "eliminada" }
  : { nuevo: "Nuevo", creado: "creado", actualizado: "actualizado", eliminado: "eliminado" });


export default function AdminJerarquia() {
  const { user } = useAuth();  // eslint-disable-line no-unused-vars
  const [tab, setTab] = useState("institutions");

  return (
    <div className="space-y-6" data-testid="admin-jerarquia">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#A43790]">Estructura</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547] flex items-center gap-2">
            <Network size={28} /> Jerarquía
          </h1>
          <p className="text-sm text-[#6E7591] mt-1">
            Organiza tu operación: Franquicia → Institución (opcional) → Colegio (opcional) → Salón → Estudiante.
            Cada salón admite hasta 15 estudiantes.
          </p>
        </div>
      </header>

      <div className="jasq-card p-2 inline-flex gap-1" data-testid="jerarquia-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={t.testid}
            className={`px-3 py-1.5 rounded-lg text-sm font-bold inline-flex items-center gap-1.5 ${tab === t.id ? "bg-[#1F2547] text-white" : "text-[#4C5478] hover:bg-[#F7F8FA]"}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "institutions" && <SimplePane entity="institutions" label="Institución" icon={Building2} />}
      {tab === "schools" && <SimplePane entity="schools" label="Colegio" icon={School} />}
      {tab === "classrooms" && <ClassroomsPane />}
    </div>
  );
}

// --------------------------- Instituciones / Colegios (panel reutilizable) ---------------------------
function SimplePane({ entity, label, icon: Icon }) {
  const [items, setItems] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get(`/${entity}`),
      api.get("/hierarchy/franchises"),
      entity === "schools" ? api.get("/institutions") : Promise.resolve({ data: [] }),
    ]);
    setItems(a.data); setFranchises(b.data); setInstitutions(c.data);
  };
  useEffect(() => { load(); }, [entity]);

  const frMap = useMemo(() => Object.fromEntries(franchises.map((s) => [s.id, s.name])), [franchises]);
  const insMap = useMemo(() => Object.fromEntries(institutions.map((i) => [i.id, i.name])), [institutions]);

  const remove = async (it) => {
    if (!confirm(`¿Eliminar "${it.name}"?`)) return;
    try {
      await api.delete(`/${entity}/${it.id}`);
      toast.success(`${label} ${g(entity).eliminado}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
  };

  return (
    <div className="jasq-card p-4 space-y-3" data-testid={`${entity}-pane`}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#6E7591]">{items.length} {label.toLowerCase()}(s)</p>
        <button onClick={() => setCreating(true)} className="jasq-btn-primary inline-flex items-center gap-1.5" data-testid={`${entity}-new-btn`}>
          <Plus size={14} /> {g(entity).nuevo} {label.toLowerCase()}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-wider font-extrabold text-[#6E7591] border-b border-[#E1E5F0]">
            <tr>
              <th className="py-2">Nombre</th>
              <th>Franquicia</th>
              {entity === "schools" && <th>Institución</th>}
              <th>Código</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-b border-[#E1E5F0]/60" data-testid={`${entity}-row-${it.id}`}>
                <td className="py-2 font-bold text-[#1F2547]"><Icon size={12} className="inline mr-1 text-[#A43790]" />{it.name}</td>
                <td className="text-[#4C5478]">{frMap[it.franchise_id] || "—"}</td>
                {entity === "schools" && <td className="text-[#4C5478]">{it.institution_id ? (insMap[it.institution_id] || "—") : "—"}</td>}
                <td className="font-mono text-[11px]">{it.code}</td>
                <td className="text-right">
                  <button onClick={() => setEditing(it)} className="text-[#2C49A0] mr-2" data-testid={`${entity}-edit-${it.id}`}><Pencil size={14} /></button>
                  <button onClick={() => remove(it)} className="text-[#A52258]" data-testid={`${entity}-del-${it.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-[#6E7591] italic">Sin {label.toLowerCase()}s</td></tr>}
          </tbody>
        </table>
      </div>
      {(creating || editing) && (
        <SimpleModal entity={entity} label={label} initial={editing} franchises={franchises} institutions={institutions}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }} />
      )}
    </div>
  );
}

function genPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let p = "";
  for (let i = 0; i < 10; i++) p += chars[Math.floor(Math.random() * chars.length)];
  return p + "!";
}

function SimpleModal({ entity, label, initial, franchises, institutions, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    franchise_id: franchises[0]?.id || "",
    institution_id: "",
    name: "",
    dni: "",
    address: "",
    contact: "",
    economic_activity: "",
    legal_representative: "",
    legal_rep_name: "",
    legal_rep_email: "",
    legal_rep_phone: "",
    legal_rep_password: "",
    ...(initial || {}),
  });
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState(null);
  const filteredInstitutions = institutions.filter((i) => i.franchise_id === form.franchise_id);
  const save = async () => {
    const err = validateName(form.name);
    if (err) { setNameError(err); toast.error(err); return; }
    setNameError(null);
    if (!form.franchise_id) { toast.error("Franquicia requerida"); return; }
    if (!form.dni.trim()) { toast.error("El DNI es obligatorio"); return; }
    if (!isEdit) {
      if (!form.legal_rep_name.trim()) { toast.error("Nombre del Representante Legal requerido"); return; }
      if (!form.legal_rep_email.trim()) { toast.error("Email del Representante Legal requerido"); return; }
    }
    setSaving(true);
    try {
      if (isEdit) {
        const body = {
          name: form.name,
          dni: form.dni,
          address: form.address,
          contact: form.contact,
          economic_activity: form.economic_activity,
          legal_representative: form.legal_representative || form.legal_rep_name,
        };
        if (entity === "schools") body.institution_id = form.institution_id || null;
        await api.patch(`/${entity}/${initial.id}`, body);
      } else {
        const body = {
          franchise_id: form.franchise_id,
          name: form.name,
          dni: form.dni,
          address: form.address,
          contact: form.contact,
          economic_activity: form.economic_activity,
          legal_rep_name: form.legal_rep_name,
          legal_rep_email: form.legal_rep_email,
          legal_rep_phone: form.legal_rep_phone || null,
          legal_rep_password: form.legal_rep_password || null,
        };
        if (entity === "schools") body.institution_id = form.institution_id || null;
        await api.post(`/${entity}`, body);
      }
      toast.success(isEdit ? `${label} ${g(entity).actualizado}` : `${label} ${g(entity).creado}`);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={isEdit ? `Editar ${label.toLowerCase()}` : `${g(entity).nuevo} ${label.toLowerCase()}`} onClose={onClose} testid={`${entity}-modal`}
      footer={<ModalActions onClose={onClose} onSave={save} saving={saving} />}>
      {!isEdit && (
        <Field label="Franquicia *">
          <select value={form.franchise_id} onChange={(e) => setForm({ ...form, franchise_id: e.target.value, institution_id: "" })} data-testid={`${entity}-franchise`}>
            <option value="">— elige —</option>
            {franchises.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
      )}
      {entity === "schools" && (
        <Field label="Institución (opcional)">
          <select value={form.institution_id || ""} onChange={(e) => setForm({ ...form, institution_id: e.target.value || null })} data-testid={`${entity}-institution`}>
            <option value="">— ninguna —</option>
            {filteredInstitutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
        </Field>
      )}
      <Field label="Nombre *" error={nameError}>
        <input value={form.name || ""} onChange={(e) => { setForm({ ...form, name: e.target.value }); if (nameError) setNameError(validateName(e.target.value)); }} onBlur={() => setNameError(validateName(form.name))} data-testid={`${entity}-name`} />
      </Field>
      <Field label="N.º de DNI *">
        <input value={form.dni || ""} onChange={(e) => setForm({ ...form, dni: e.target.value })} data-testid={`${entity}-dni`} placeholder="Único por franquicia" />
      </Field>
      <Field label="Dirección">
        <input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid={`${entity}-address`} />
      </Field>
      <Field label="Contacto">
        <input value={form.contact || ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid={`${entity}-contact`} placeholder="Teléfono / correo de contacto" />
      </Field>
      <Field label="Actividad económica">
        <input value={form.economic_activity || ""} onChange={(e) => setForm({ ...form, economic_activity: e.target.value })} data-testid={`${entity}-economic`} />
      </Field>

      {/* Representante Legal → se crea como Administrador (franquiciado) de la franquicia (#B4) */}
      {isEdit ? (
        <Field label="Representante Legal">
          <input value={form.legal_representative || ""} onChange={(e) => setForm({ ...form, legal_representative: e.target.value })} data-testid={`${entity}-legalrep-name-edit`} />
          {form.legal_rep_email && <p className="text-[11px] text-[#6E7591] mt-1">Admin vinculado: {form.legal_rep_email}</p>}
        </Field>
      ) : (
        <div className="rounded-xl border-2 border-dashed border-[#E1E5F0] p-3 space-y-2 bg-[#FAFBFD]" data-testid={`${entity}-legalrep-block`}>
          <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">Representante Legal (se crea como Administrador de la franquicia)</p>
          <Field label="Nombre completo *">
            <input value={form.legal_rep_name || ""} onChange={(e) => setForm({ ...form, legal_rep_name: e.target.value })} data-testid={`${entity}-legalrep-name`} />
          </Field>
          <Field label="Email *">
            <input type="email" value={form.legal_rep_email || ""} onChange={(e) => setForm({ ...form, legal_rep_email: e.target.value })} data-testid={`${entity}-legalrep-email`} placeholder="Si ya existe, se reutiliza" />
          </Field>
          <Field label="Teléfono">
            <input value={form.legal_rep_phone || ""} onChange={(e) => setForm({ ...form, legal_rep_phone: e.target.value })} data-testid={`${entity}-legalrep-phone`} />
          </Field>
          <Field label="Contraseña (sólo si es usuario nuevo)">
            <div className="flex gap-2">
              <input value={form.legal_rep_password || ""} onChange={(e) => setForm({ ...form, legal_rep_password: e.target.value })} data-testid={`${entity}-legalrep-password`} placeholder="mín. 6 caracteres" />
              <button type="button" onClick={() => setForm({ ...form, legal_rep_password: genPassword() })} className="jasq-btn-ghost text-xs whitespace-nowrap" data-testid={`${entity}-legalrep-genpwd`}>Auto-generar</button>
            </div>
            {form.legal_rep_password && <p className="text-[11px] text-[#9C7A18] mt-1 font-bold">Guarda esta contraseña: {form.legal_rep_password}</p>}
          </Field>
        </div>
      )}
    </Modal>
  );
}

// --------------------------- Salones (con aforo) ---------------------------
function ClassroomsPane() {
  const [items, setItems] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [schools, setSchools] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [filterFr, setFilterFr] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const [a, b, c, d] = await Promise.all([
      api.get("/classrooms"), api.get("/hierarchy/franchises"), api.get("/schools"), api.get("/institutions"),
    ]);
    setItems(a.data); setFranchises(b.data); setSchools(c.data); setInstitutions(d.data);
  };
  useEffect(() => { load(); }, []);

  const frMap = useMemo(() => Object.fromEntries(franchises.map((s) => [s.id, s.name])), [franchises]);
  const schoolMap = useMemo(() => Object.fromEntries(schools.map((s) => [s.id, s.name])), [schools]);
  const insMap = useMemo(() => Object.fromEntries(institutions.map((i) => [i.id, i.name])), [institutions]);
  const visible = filterFr ? items.filter((c) => c.franchise_id === filterFr) : items;

  const remove = async (c) => {
    if (!confirm(`¿Eliminar salón "${c.name}"?`)) return;
    try {
      await api.delete(`/classrooms/${c.id}`);
      toast.success("Salón eliminado");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
  };

  return (
    <div className="jasq-card p-4 space-y-3" data-testid="classrooms-pane">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <p className="text-sm text-[#6E7591]">{visible.length} salón(es)</p>
          {franchises.length > 1 && (
            <select value={filterFr} onChange={(e) => setFilterFr(e.target.value)} className="text-xs border-2 border-[#E1E5F0] rounded px-2 py-1" data-testid="classrooms-filter-franchise">
              <option value="">Todas las franquicias</option>
              {franchises.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>
        <button onClick={() => setCreating(true)} className="jasq-btn-primary inline-flex items-center gap-1.5" data-testid="classroom-new-btn">
          <Plus size={14} /> Nuevo salón
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {visible.map((c) => {
          const pct = Math.round(((c.students_count || 0) / (c.capacity || 15)) * 100);
          const isFull = c.students_count >= (c.capacity || 15);
          return (
            <div key={c.id} className="rounded-xl border-2 border-[#E1E5F0] bg-white p-3 space-y-2" data-testid={`classroom-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-display text-base font-extrabold text-[#1F2547]">{c.name}</p>
                  <p className="text-[10px] text-[#6E7591] font-mono">{c.code}</p>
                  <p className="text-[11px] text-[#4C5478] mt-0.5">
                    <ChevronRight size={10} className="inline" />{frMap[c.franchise_id] || "—"}
                    {c.school_id && <> · {schoolMap[c.school_id]}</>}
                    {c.institution_id && !c.school_id && <> · {insMap[c.institution_id]}</>}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(c)} className="text-[#2C49A0]" data-testid={`classroom-edit-${c.id}`}><Pencil size={14} /></button>
                  <button onClick={() => remove(c)} className="text-[#A52258]" data-testid={`classroom-del-${c.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-[11px] font-bold">
                  <span className="text-[#6E7591] inline-flex items-center gap-1"><UsersIcon size={11} /> Aforo</span>
                  <span className={isFull ? "text-[#A52258]" : "text-[#1F2547]"}>{c.students_count || 0}/{c.capacity || 15}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#F7F8FA] overflow-hidden">
                  <div className={`h-full ${isFull ? "bg-[#A52258]" : pct > 80 ? "bg-[#F5B82E]" : "bg-[#3FA84D]"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
              </div>
            </div>
          );
        })}
        {visible.length === 0 && <p className="col-span-full text-center text-[#6E7591] italic py-6">Sin salones</p>}
      </div>
      {(creating || editing) && (
        <ClassroomModal initial={editing} franchises={franchises} schools={schools} institutions={institutions}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }} />
      )}
    </div>
  );
}

function ClassroomModal({ initial, franchises, schools, institutions, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    franchise_id: franchises[0]?.id || "",
    institution_id: "",
    school_id: "",
    name: "",
    grade: "",
    capacity: 15,
    ...(initial || {}),
  });
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState(null);
  const filteredSchools = schools.filter((s) => s.franchise_id === form.franchise_id && (!form.institution_id || s.institution_id === form.institution_id));
  const filteredInstitutions = institutions.filter((i) => i.franchise_id === form.franchise_id);
  const save = async () => {
    const err = validateName(form.name);
    if (err) { setNameError(err); toast.error(err); return; }
    setNameError(null);
    if (!form.franchise_id) { toast.error("Franquicia requerida"); return; }
    setSaving(true);
    try {
      const body = {
        franchise_id: form.franchise_id,
        institution_id: form.institution_id || null,
        school_id: form.school_id || null,
        name: form.name,
        grade: form.grade,
        capacity: Math.min(parseInt(form.capacity || 15, 10) || 15, 15),
      };
      if (isEdit) {
        delete body.franchise_id;
        await api.patch(`/classrooms/${initial.id}`, body);
      } else {
        await api.post("/classrooms", body);
      }
      toast.success(isEdit ? "Salón actualizado" : "Salón creado");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={isEdit ? "Editar salón" : "Nuevo salón"} onClose={onClose} testid="classroom-modal"
      footer={<ModalActions onClose={onClose} onSave={save} saving={saving} />}>
      {!isEdit && (
        <Field label="Franquicia *">
          <select value={form.franchise_id} onChange={(e) => setForm({ ...form, franchise_id: e.target.value, institution_id: "", school_id: "" })} data-testid="classroom-franchise">
            <option value="">— elige —</option>
            {franchises.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
      )}
      <Field label="Institución (opcional)">
        <select value={form.institution_id || ""} onChange={(e) => setForm({ ...form, institution_id: e.target.value || null, school_id: "" })} data-testid="classroom-institution">
          <option value="">— ninguna —</option>
          {filteredInstitutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
      </Field>
      <Field label="Colegio (opcional)">
        <select value={form.school_id || ""} onChange={(e) => setForm({ ...form, school_id: e.target.value || null })} data-testid="classroom-school">
          <option value="">— ninguno —</option>
          {filteredSchools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </Field>
      <Field label="Nombre *" error={nameError}>
        <input value={form.name || ""} onChange={(e) => { setForm({ ...form, name: e.target.value }); if (nameError) setNameError(validateName(e.target.value)); }} onBlur={() => setNameError(validateName(form.name))} data-testid="classroom-name" />
      </Field>
      <Field label="Grado / Curso"><input value={form.grade || ""} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="Ej. 5° Primaria" data-testid="classroom-grade" /></Field>
      <Field label="Aforo (máx. 15)">
        <input type="number" min={1} max={15} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="classroom-capacity" />
      </Field>
      <p className="text-[11px] text-[#6E7591] italic">El código se genera automáticamente.</p>
    </Modal>
  );
}

// --------------------------- Primitivos UI ---------------------------
function Modal({ title, onClose, children, footer, testid }) {
  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-[#1F2547]/60 backdrop-blur-sm flex justify-end"
      data-testid={testid}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="jasq-drawer-panel w-full max-w-md h-full bg-white shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="px-5 py-4 border-b border-[#E1E5F0] flex items-center justify-between shrink-0 bg-white">
          <h3 className="font-display text-lg font-extrabold text-[#1F2547]">{title}</h3>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]" data-testid={`${testid}-close`}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto flex-1 min-h-0">{children}</div>
        {footer && (
          <div className="shrink-0 border-t border-[#E1E5F0] bg-white px-5 py-3">{footer}</div>
        )}
      </div>
    </div>,
    document.body
  );
}
function Field({ label, children, error }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790]">{label}</span>
      <div className="mt-1 [&_input]:w-full [&_input]:px-3 [&_input]:py-2 [&_input]:rounded-lg [&_input]:border-2 [&_input]:border-[#E1E5F0] [&_input]:text-sm [&_select]:w-full [&_select]:px-3 [&_select]:py-2 [&_select]:rounded-lg [&_select]:border-2 [&_select]:border-[#E1E5F0] [&_select]:text-sm [&_textarea]:w-full [&_textarea]:px-3 [&_textarea]:py-2 [&_textarea]:rounded-lg [&_textarea]:border-2 [&_textarea]:border-[#E1E5F0] [&_textarea]:text-sm">
        {children}
      </div>
      {error && <p className="mt-1 text-[11px] font-bold text-[#A52258]" data-testid="field-error">{error}</p>}
    </label>
  );
}
function ModalActions({ onClose, onSave, saving }) {
  return (
    <div className="flex items-center gap-3">
      <button onClick={onClose} className="jasq-btn-ghost" data-testid="modal-cancel">Cancelar</button>
      <div className="flex-1" />
      <button onClick={onSave} disabled={saving} className="jasq-btn-primary" data-testid="modal-save">{saving ? "Guardando…" : "Guardar"}</button>
    </div>
  );
}
